import { Controller } from 'stimulus';
import { calculateBillableTotals } from '@/js/shared/billables/totals';
import { formatCurrency } from '@/js/shared/billables/currency';
import { decimal, money } from '@/js/shared/billables/decimal';
import { setDisabled } from '@/js/modules/set_disabled';

export default class extends Controller {
  static targets = [
    'allCheckbox',
    'itemCheckbox',
    'amountField',
    'taxCheckbox',
    'subtotal',
    'taxAmount',
    'refundTotal',
    'exceedsError',
    'shippingField',
    'shippingTaxField',
  ];

  static values = {
    taxRate: String,
    availableForRefund: String,
    shippingAvailable: String,
    shippingTaxAvailable: String,
    currencySymbol: String,
    currencySide: String,
  };

  connect() {
    this.recalculate();
  }

  toggleAll() {
    this.itemCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = this.allCheckboxTarget.checked;
      this.#syncAmountField(checkbox);
    });

    this.recalculate();
  }

  toggleItem(event) {
    this.#syncAmountField(event.target);
    this.recalculate();
  }

  amountChanged(event) {
    const field = event.target;
    this.#itemCheckboxFor(this.#rowFor(field)).checked = decimal(field.value || '0').gt('0');
    this.recalculate();
  }

  recalculate() {
    const result = calculateBillableTotals('refund', this.#calculationInput);

    this.#syncAllCheckbox();
    this.#renderTotals(result);

    const invalid = this.#renderErrors(result) || decimal(result.total).lte('0');
    this.#toggleButtons(invalid);
  }

  get #calculationInput() {
    return {
      items: this.itemCheckboxTargets.map((checkbox) => {
        const row = this.#rowFor(checkbox);

        return {
          amount: this.#amountFieldFor(row).value || '0',
          refunded: checkbox.checked,
          taxed: this.#taxCheckboxFor(row)?.checked || false,
        };
      }),
      taxRate: this.taxRateValue || '0',
      shipping: this.hasShippingFieldTarget ? this.shippingFieldTarget.value || '0' : '0',
      shippingTax: this.hasShippingTaxFieldTarget ? this.shippingTaxFieldTarget.value || '0' : '0',
    };
  }

  #syncAmountField(checkbox) {
    const row = this.#rowFor(checkbox);
    this.#amountFieldFor(row).value = checkbox.checked
      ? money(row.dataset.clientPrice).toFixed(2)
      : '0.00';
  }

  #syncAllCheckbox() {
    this.allCheckboxTarget.checked =
      this.itemCheckboxTargets.length > 0 &&
      this.itemCheckboxTargets.every((checkbox) => checkbox.checked);
  }

  #renderTotals(result) {
    const currency = { symbol: this.currencySymbolValue, side: this.currencySideValue };

    this.subtotalTarget.textContent = formatCurrency(result.subtotal, currency, {
      useGrouping: false,
    });
    if (this.hasTaxAmountTarget) {
      this.taxAmountTarget.textContent = formatCurrency(result.tax, currency, {
        useGrouping: false,
      });
    }
    this.refundTotalTarget.textContent = formatCurrency(result.total, currency);
  }

  #renderErrors(result) {
    let rowError = false;

    this.itemCheckboxTargets.forEach((checkbox) => {
      const row = this.#rowFor(checkbox);
      const field = this.#amountFieldFor(row);
      const exceeds = decimal(field.value || '0').gt(money(row.dataset.clientPrice));

      row.classList.toggle('billable-item--refund-error', exceeds);
      field.classList.toggle('field--error', exceeds);
      rowError = rowError || exceeds;
    });

    let shippingError = false;
    if (this.hasShippingFieldTarget) {
      shippingError = this.#toggleFieldError(this.shippingFieldTarget, this.shippingAvailableValue);
    }
    if (this.hasShippingTaxFieldTarget) {
      shippingError =
        this.#toggleFieldError(this.shippingTaxFieldTarget, this.shippingTaxAvailableValue) ||
        shippingError;
    }

    const exceedsAvailable = decimal(result.total).gt(money(this.availableForRefundValue || '0'));
    this.exceedsErrorTarget.classList.toggle('hide', !exceedsAvailable);

    return rowError || shippingError || exceedsAvailable;
  }

  #toggleFieldError(field, available) {
    const exceeds = decimal(field.value || '0').gt(money(available || '0'));
    field.classList.toggle('field--error', exceeds);
    return exceeds;
  }

  #toggleButtons(invalid) {
    this.#saveButtons.forEach((button) => {
      setDisabled(button, invalid);
    });

    const sendButton = this.#sendButton;
    if (!sendButton) {
      return;
    }

    // The selected client's pill, inside the selector of either header layout.
    const noClient = !this.element.querySelector(
      '[data-controller~="clients--client-selector"] [data-client-id]'
    );
    const disabled = invalid || noClient;
    setDisabled(sendButton, disabled);

    if (!disabled) {
      sendButton.removeAttribute('title');
    } else if (invalid) {
      sendButton.setAttribute('title', 'Add a refund amount before sending.');
    } else {
      sendButton.setAttribute('title', "You haven't added any clients to this project yet.");
    }
  }

  get #sendButton() {
    return this.element.querySelector('[data-billable-actions-role="send"]');
  }

  get #saveButtons() {
    return this.element.querySelectorAll('[data-billable-actions-role="save"]');
  }

  #rowFor(element) {
    return element.closest('.billable-item');
  }

  #itemCheckboxFor(row) {
    return this.itemCheckboxTargets.find((checkbox) => this.#rowFor(checkbox) === row);
  }

  #amountFieldFor(row) {
    return this.amountFieldTargets.find((field) => this.#rowFor(field) === row);
  }

  #taxCheckboxFor(row) {
    return this.taxCheckboxTargets.find((checkbox) => this.#rowFor(checkbox) === row);
  }
}
